/**
 * FR4 — per-project generation settings (model / thinking effort / output
 * budget), persisted in localStorage (user asked «сохранять локально») and
 * passed EXPLICITLY into backend calls as `generation: {...}` — never a
 * silent provider switch.
 */

import { useCallback, useEffect, useState } from 'react';

export interface AdCreativeGenSettings {
  /** Text-generation model override (Google family v1); undefined = default. */
  textModel?: string;
  /** undefined = per-call default (today's literals). */
  reasoningEffort?: 'low' | 'medium' | 'high';
  /** undefined = per-call default; UI presets 8k/16k/32k. */
  maxOutputTokens?: number;
}

/** F4 pairing floors (mirror of the server map — UI cannot express a combo the
 * server would reject). */
export const UI_EFFORT_BUDGET_FLOORS: Record<'low' | 'medium' | 'high', number> = {
  low: 8_000,
  medium: 12_000,
  high: 16_000,
};

export function genSettingsStorageKey(projectId: string): string {
  return `adCreative.genSettings.${projectId}`;
}

function readSettings(projectId: string): AdCreativeGenSettings {
  try {
    const raw = localStorage.getItem(genSettingsStorageKey(projectId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as AdCreativeGenSettings;
    return {
      textModel: typeof parsed.textModel === 'string' && parsed.textModel ? parsed.textModel : undefined,
      reasoningEffort: parsed.reasoningEffort === 'low' || parsed.reasoningEffort === 'medium' || parsed.reasoningEffort === 'high'
        ? parsed.reasoningEffort
        : undefined,
      maxOutputTokens: typeof parsed.maxOutputTokens === 'number' && parsed.maxOutputTokens > 0
        ? parsed.maxOutputTokens
        : undefined,
    };
  } catch {
    return {};
  }
}

export function useAdCreativeGenSettings(projectId: string): {
  settings: AdCreativeGenSettings;
  update: (patch: Partial<AdCreativeGenSettings>) => void;
  reset: () => void;
  /** Request payload (`generation` field) or undefined when all defaults. */
  requestPayload: AdCreativeGenSettings | undefined;
} {
  const [settings, setSettings] = useState<AdCreativeGenSettings>(() => readSettings(projectId));

  useEffect(() => {
    setSettings(readSettings(projectId));
  }, [projectId]);

  const update = useCallback((patch: Partial<AdCreativeGenSettings>) => {
    setSettings((current) => {
      const next: AdCreativeGenSettings = { ...current, ...patch };
      // F4 pairing: raising effort floors the budget so the server never sees
      // a contradictory explicit combo from this UI.
      if (next.reasoningEffort && next.maxOutputTokens != null
        && next.maxOutputTokens < UI_EFFORT_BUDGET_FLOORS[next.reasoningEffort]) {
        next.maxOutputTokens = UI_EFFORT_BUDGET_FLOORS[next.reasoningEffort];
      }
      try {
        if (next.textModel || next.reasoningEffort || next.maxOutputTokens != null) {
          localStorage.setItem(genSettingsStorageKey(projectId), JSON.stringify(next));
        } else {
          localStorage.removeItem(genSettingsStorageKey(projectId));
        }
      } catch {
        // storage unavailable — settings still apply in-session
      }
      return next;
    });
  }, [projectId]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(genSettingsStorageKey(projectId));
    } catch {
      // ignore
    }
    setSettings({});
  }, [projectId]);

  const requestPayload = settings.textModel || settings.reasoningEffort || settings.maxOutputTokens != null
    ? settings
    : undefined;

  return { settings, update, reset, requestPayload };
}
